import type { Chapter, ChapterRef } from "../data-model/novel.js";
import type { ChapterIndex } from "../data-model/primitives.js";
import type { ChapterFailure } from "../data-model/result.js";
import type { ChapterFetchError } from "./errors.js";
import type { ResumeState, ResumeFailureRecord } from "../storage/resume-state.js";

/**
 * In-memory chapter bookkeeping for one download run: enqueue dedup, the
 * success/failure ledger, and the single projection onto the persisted
 * ResumeState shape. Pure state — no I/O, no events, no timers; DownloadRun
 * owns persistence and the event bus.
 */
export class RunLedger {
  readonly #successes: Chapter[] = [];
  readonly #failures: ChapterFailure[] = [];
  readonly #completedSet = new Set<ChapterIndex>();
  readonly #completedList: ChapterIndex[] = [];
  readonly #failedMap = new Map<ChapterIndex, ResumeFailureRecord>();
  readonly #enqueuedSet = new Set<ChapterIndex>();

  private constructor() {}

  static seed(state: ResumeState | null, priorChapters: readonly Chapter[]): RunLedger {
    const ledger = new RunLedger();
    ledger.#successes.push(...priorChapters);
    for (const idx of state?.completed ?? []) ledger.#markCompleted(idx);
    for (const chapter of priorChapters) ledger.#markCompleted(chapter.index);
    for (const f of state?.failed ?? []) {
      if (!ledger.#completedSet.has(f.index)) ledger.#failedMap.set(f.index, f);
    }
    return ledger;
  }

  #markCompleted(index: ChapterIndex): void {
    if (this.#completedSet.has(index)) return;
    this.#completedSet.add(index);
    this.#completedList.push(index);
  }

  /** Check-and-set enqueue dedup: false if the index is completed or already enqueued. */
  markEnqueued(index: ChapterIndex): boolean {
    if (this.#completedSet.has(index) || this.#enqueuedSet.has(index)) return false;
    this.#enqueuedSet.add(index);
    return true;
  }

  recordSuccess(chapter: Chapter): void {
    this.#successes.push(chapter);
    this.#markCompleted(chapter.index);
    this.#failedMap.delete(chapter.index);
  }

  recordFailure(ref: ChapterRef, error: ChapterFetchError): void {
    this.#failures.push({ ref, error, attempts: 1 });
    this.#failedMap.set(ref.index, {
      index: ref.index,
      lastError: { code: error.code, message: error.message },
    });
  }

  dropOrphanFailure(index: ChapterIndex): void {
    this.#failedMap.delete(index);
  }

  /** The ONLY projection to the persisted shape; nothing else writes state.completed/failed. */
  applyTo(state: ResumeState): ResumeState {
    return {
      ...state,
      completed: [...this.#completedList].sort((a, b) => Number(a) - Number(b)),
      failed: [...this.#failedMap.values()],
    };
  }

  successes(): readonly Chapter[] {
    return this.#successes;
  }

  failures(): readonly ChapterFailure[] {
    return this.#failures;
  }

  /** Snapshot — safe to mutate the ledger while iterating the result. */
  failedRecords(): readonly ResumeFailureRecord[] {
    return [...this.#failedMap.values()];
  }

  /** Fetched-chapter count (including prior-run chapters) — drives the progress event. */
  get completedCount(): number {
    return this.#successes.length;
  }

  get enqueuedCount(): number {
    return this.#enqueuedSet.size;
  }
}
